import { useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useNativeChrome } from '../hooks/useNativeChrome';
import { useHaptics } from '../hooks/useHaptics';
import { useAuth } from '../context/AuthContext';
import { BrandMark } from '../components/ui/bits';
import { authMessage } from '../components/auth/parts';
import { AppleLogo, GoogleLogo, Mail } from '../components/ui/icons';
import soccerEmoji from '../assets/emoji/soccer.svg';
import trophyEmoji from '../assets/emoji/trophy.svg';
import basketballEmoji from '../assets/emoji/basketball.svg';
import targetEmoji from '../assets/emoji/target.svg';
import footballEmoji from '../assets/emoji/football.svg';
import cyclingEmoji from '../assets/emoji/cycling.svg';

// ── Ambient sport emoji ──────────────────────────────────────────────────────
// A soft, multi-sport backdrop that says "this isn't only football". Each glyph is a
// Google (Noto) emoji bundled as an SVG, so it renders identically on every platform
// (never the device's native emoji), and drifts on its own loop (see `h2hfloatA` /
// `h2hfloatB` in index.css). Purely decorative, behind the content and never interactive.
type Glyph = 'soccer' | 'trophy' | 'basketball' | 'target' | 'football' | 'cycling';

const GLYPH_EMOJI: Record<Glyph, string> = {
  soccer: soccerEmoji,
  trophy: trophyEmoji,
  basketball: basketballEmoji,
  target: targetEmoji,
  football: footballEmoji,
  cycling: cyclingEmoji
};

type FloatIcon = {
  glyph: Glyph;
  size: number;
  /** Kept low so the colourful emoji read as a backdrop, not foreground clutter. */
  opacity: number;
  rotate: string;
  drift: 'A' | 'B';
  duration: string;
  pos: CSSProperties;
};

// Coordinates mirror the design export (a 366×812 screen): a faint top band around
// the logo, a more prominent middle band, and a soft scatter near the CTA.
const FLOAT_ICONS: FloatIcon[] = [
  { glyph: 'soccer', size: 30, opacity: 0.42, rotate: '-15deg', drift: 'A', duration: '6.6s', pos: { left: 26, top: 120 } },
  { glyph: 'trophy', size: 26, opacity: 0.4, rotate: '11deg', drift: 'B', duration: '7.9s', pos: { right: 30, top: 124 } },
  { glyph: 'basketball', size: 28, opacity: 0.42, rotate: '14deg', drift: 'A', duration: '8.3s', pos: { right: 40, top: 200 } },
  { glyph: 'target', size: 24, opacity: 0.34, rotate: '0deg', drift: 'B', duration: '7.1s', pos: { left: 34, top: 236 } },
  { glyph: 'football', size: 26, opacity: 0.36, rotate: '-22deg', drift: 'A', duration: '8.6s', pos: { left: 122, top: 300 } },
  { glyph: 'cycling', size: 28, opacity: 0.38, rotate: '9deg', drift: 'B', duration: '7.4s', pos: { right: 128, top: 298 } },
  { glyph: 'cycling', size: 58, opacity: 0.6, rotate: '-9deg', drift: 'B', duration: '7s', pos: { left: 30, top: 394 } },
  { glyph: 'football', size: 60, opacity: 0.6, rotate: '26deg', drift: 'A', duration: '8.5s', pos: { right: 32, top: 382 } },
  { glyph: 'basketball', size: 34, opacity: 0.46, rotate: '-18deg', drift: 'B', duration: '6.8s', pos: { left: 148, top: 456 } },
  { glyph: 'soccer', size: 30, opacity: 0.44, rotate: '20deg', drift: 'A', duration: '7.6s', pos: { right: 206, top: 470 } },
  { glyph: 'target', size: 24, opacity: 0.36, rotate: '0deg', drift: 'B', duration: '6.4s', pos: { left: 70, top: 504 } },
  { glyph: 'trophy', size: 26, opacity: 0.34, rotate: '8deg', drift: 'A', duration: '7.3s', pos: { right: 150, top: 524 } }
];

function FloatingSports() {
  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden>
      {FLOAT_ICONS.map((icon, i) => (
        <img
          key={i}
          src={GLYPH_EMOJI[icon.glyph]}
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            ...icon.pos,
            width: icon.size,
            height: icon.size,
            opacity: icon.opacity,
            ['--r' as string]: icon.rotate,
            animation: `h2hfloat${icon.drift} ${icon.duration} ease-in-out infinite alternate`
          }}
        />
      ))}
    </div>
  );
}

export default function Welcome() {
  useNativeChrome();
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const { signInWithGoogle, signInWithApple } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Apple/Google sign-in only ships in the native apps (they use the OS sheets via
  // the Capacitor plugin). On the web we keep it to email until the web OAuth flow
  // is wired up for the new domains.
  const isNative = Capacitor.isNativePlatform();

  // Social sign-in resolves the app gate on success; on failure we surface friendly
  // copy and re-enable the buttons.
  async function social(fn: () => Promise<void>) {
    setError(null);
    setBusy(true);
    impact('medium');
    try {
      await fn();
    } catch (err) {
      setError(authMessage(err));
      setBusy(false);
    }
  }

  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-white text-ink">
      {/* soft green halo behind the logo + headline */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[300px]"
        style={{
          background:
            'radial-gradient(118% 60% at 50% -10%, rgba(36,197,106,0.16) 0%, rgba(255,255,255,0) 52%)'
        }}
      />

      <div className="relative mx-auto flex w-full max-w-app flex-1 flex-col px-safe pt-safe pb-safe">
        <FloatingSports />

        {/* logo lockup */}
        <div className="relative z-10 flex flex-col items-center pt-[42px]">
          <BrandMark size={62} className="rounded-[18px] shadow-[0_16px_30px_-12px_rgba(0,0,0,0.5)]" />
          <span className="mt-[13px] text-[16px] font-extrabold tracking-tight text-ink">
            HeadToHead
          </span>
        </div>

        {/* hero copy */}
        <div className="relative z-10 px-2 pt-[26px] text-center">
          <h1 className="text-[36px] font-extrabold leading-[1.0] tracking-[-0.045em] text-ink">
            Build your cards.
            <br />
            Climb the ladder.
          </h1>
          <p className="mx-auto mt-[13px] max-w-[320px] text-[15px] font-medium leading-[1.5] text-[#6E6E73]">
            Stack boosts on football, rank your cycling duels — then watch your points go live against
            friends in your division.
          </p>
        </div>

        <div className="flex-1" />

        {/* auth */}
        <div className="relative z-10 pt-6">
          {error && (
            <p className="mb-3 rounded-xl bg-miss/10 px-3 py-2 text-center text-[13px] font-semibold text-danger">
              {error}
            </p>
          )}

          {/* primary — create account */}
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              impact('medium');
              navigate('/onboarding');
            }}
            className="tap flex w-full items-center justify-center rounded-2xl py-[17px] text-[16px] font-extrabold tracking-snug text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_18px_34px_-14px_rgba(36,197,106,0.6)] disabled:opacity-50"
            style={{ background: 'linear-gradient(140deg,#2EDB7B,#0BA078)' }}
          >
            Create free account
          </button>

          {/* apple + google — native apps only (web uses email for now) */}
          {isNative && (
            <>
              {/* divider */}
              <div className="my-4 flex items-center gap-3">
                <span className="h-px flex-1 bg-black/[0.09]" />
                <span className="text-[12px] font-semibold text-ink3">or continue with</span>
                <span className="h-px flex-1 bg-black/[0.09]" />
              </div>

              <div className="flex gap-[10px]">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => social(signInWithApple)}
                  className="tap flex flex-1 items-center justify-center gap-2 rounded-[14px] bg-[#111114] py-[14px] text-[14px] font-bold tracking-snug text-white shadow-[0_10px_22px_-14px_rgba(0,0,0,0.5)] disabled:opacity-50"
                >
                  <AppleLogo width={17} height={17} className="-mt-[2px]" />
                  Apple
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => social(signInWithGoogle)}
                  className="tap flex flex-1 items-center justify-center gap-2 rounded-[14px] border border-black/[0.12] bg-white py-[14px] text-[14px] font-bold tracking-snug text-ink disabled:opacity-50"
                >
                  <GoogleLogo width={17} height={17} />
                  Google
                </button>
              </div>
            </>
          )}

          {/* email */}
          <button
            type="button"
            disabled={busy}
            onClick={() => navigate('/signup')}
            className="tap mt-[10px] flex w-full items-center justify-center gap-[9px] rounded-[14px] border border-black/[0.12] bg-white py-[14px] text-[14px] font-bold tracking-snug text-ink disabled:opacity-50"
          >
            <Mail width={17} height={17} strokeWidth={1.9} />
            Continue with email
          </button>

          {/* log in — clear, prominent entry for returning players */}
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              impact('medium');
              navigate('/login');
            }}
            className="tap mt-[10px] flex w-full items-center justify-center rounded-[14px] border-[1.5px] border-accent-ink/30 bg-white py-[14px] text-[15px] font-extrabold tracking-snug text-accent-ink disabled:opacity-50"
          >
            Log in
          </button>

          <p className="px-4 pt-[14px] text-center text-[11px] font-medium leading-[1.5] text-[#9A9AA4]">
            By continuing you agree to our{' '}
            <a
              href="https://headtohead.dk/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-ink2 underline"
            >
              Terms
            </a>{' '}
            &{' '}
            <a
              href="https://headtohead.dk/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-ink2 underline"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
